import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ProductViewer from './ProductViewer.js';
import AccessControl from './AccessControl.js';
import Header from './components/layout/Header.js';
import MobileFooter from './components/layout/MobileFooter.js';
import DesktopSidebar from './components/layout/DesktopSidebar.js';
import AdminConfigSidebar from './components/admin/AdminConfigSidebar.js';
import PriceListGenerator from './components/admin/PriceListGenerator.js';
import NavigationSection from './components/navigation/NavigationSection.js';
import PopupManager from './components/popups/PopupManager.js';
import { CompulsoryBanner } from './components/popups/CompulsoryPopup.js';
import { createProductKey } from './AppUtils.js';
import { isAdminMode } from './constants.js';
import { useProductData } from './hooks/useProductData.js';
import { useProductSelection } from './hooks/useProductSelection.js';
import { useNavigation } from './hooks/useNavigation.js';
import useScrollControls from './hooks/useScrollControls.js';
import { useInitialization, useProductIndexSync } from './hooks/useInitialization.js';
import { useUrlSync } from './hooks/useUrlSync.js';
import { useProductHandlers } from './hooks/useProductHandlers.js';
import { useCompulsoryCategories } from './hooks/useCompulsoryCategories.js';

export default function VesaroConfigurator() {
  // Access control state
  const [hasAccess, setHasAccess] = useState(false);

  // Check if we're showing the price list view
  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get('view');
  const showPriceList = isAdminMode() && viewParam === 'prices';

  // Data and selection hooks
  const { productData, mainCategories, categoryConfig, loading, error, fetchProducts } = useProductData();
  const {
    selectedProducts,
    totalPrice,
    totalTestPrice,
    toggleProduct,
    removeProduct,
    setPreselectedProducts,
    addProductWithRequirements,
    getProductCompatibilityStatus,
    sortProductsByCompatibility,
    initializeWithLockedProducts,
    switchBrandEcosystem,
    switchDisplayEcosystem
  } = useProductSelection(productData, categoryConfig);

  // Compulsory categories hook - NEW
  const {
    pendingCompulsory,
    initializeCompulsoryData,
    handleCompulsoryRemoval,
    handleProductAdded,
    shouldBlockNavigation,
    getMissingCompulsoryItems,
    getCompulsoryStatus,
    isCategoryCompulsory,
    isSubcategoryCompulsory
  } = useCompulsoryCategories(selectedProducts, categoryConfig);

  // Compulsory banner shake state - defined before useNavigation so it can be passed
  const [compulsoryBannerShake, setCompulsoryBannerShake] = useState(false);
  // Track if user has dismissed the banner (it reappears on shake)
  const [compulsoryBannerDismissed, setCompulsoryBannerDismissed] = useState(false);

  // Trigger shake animation on compulsory banner (also un-dismisses it)
  const triggerCompulsoryShake = useCallback(() => {
    setCompulsoryBannerDismissed(false); // Show banner again
    // Use requestAnimationFrame to ensure banner is visible before shaking
    requestAnimationFrame(() => {
      setCompulsoryBannerShake(true);
      setTimeout(() => setCompulsoryBannerShake(false), 500);
    });
  }, []);

  // Navigation hook - NOW WITH COMPULSORY BLOCKING
  const {
    selectedMainCategory,
    selectedSubCategory,
    selectedProduct,
    viewState,
    setSelectedProduct,
    setSelectedMainCategory,
    setSelectedSubCategory,
    setViewState,
    hasRealSubCategories,
    getCurrentSubCategories,
    handleMainCategoryClick,
    handleSubCategoryClick,
    handleBack,
    getSelectableCategories
  } = useNavigation(mainCategories, productData, categoryConfig, shouldBlockNavigation, triggerCompulsoryShake);

  // Scroll controls hook
  const {
    categoryScrollRef,
    categoryScrollState,
    scrollCategoriesLeft,
    scrollCategoriesRight,
    triggerCategoryCheck,
    productScrollRef,
    productScrollState,
    scrollProducts,
    triggerProductCheck,
    shouldShowArrows
  } = useScrollControls();

  // UI state
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [infoPopupProduct, setInfoPopupProduct] = useState(null);
  const [showRequirementPopup, setShowRequirementPopup] = useState(false);
  const [requirementData, setRequirementData] = useState(null);
  const [showBrandConflictPopup, setShowBrandConflictPopup] = useState(false);
  const [brandConflictData, setBrandConflictData] = useState(null);
  const [showDisplayConflictPopup, setShowDisplayConflictPopup] = useState(false);
  const [displayConflictData, setDisplayConflictData] = useState(null);
  const [showSizeConflictPopup, setShowSizeConflictPopup] = useState(false);
  const [sizeConflictData, setSizeConflictData] = useState(null);
  const [showMobileBuildSummary, setShowMobileBuildSummary] = useState(false);
  const [showLockedPopup, setShowLockedPopup] = useState(false);
  const [lockedMessage, setLockedMessage] = useState('');
  const [storedBaseConfig, setStoredBaseConfig] = useState(null);
  const [showEmailSalesForm, setShowEmailSalesForm] = useState(false);
  const [showHintArrow, setShowHintArrow] = useState(true);
  
  // NEW: Missing compulsory popup state
  const [showMissingCompulsoryPopup, setShowMissingCompulsoryPopup] = useState(false);
  const [missingCompulsoryItems, setMissingCompulsoryItems] = useState([]);

  // Initialize data from URL or base config - NOW WITH COMPULSORY
  useInitialization({
    hasAccess,
    fetchProducts,
    setStoredBaseConfig,
    initializeWithLockedProducts,
    setPreselectedProducts,
    initializeCompulsoryData  // NEW: Pass compulsory initializer
  });

  // Helper functions
  function getProductInBuild() {
    if (!selectedMainCategory || !selectedSubCategory) return null;
    const categoryKey = createProductKey(selectedMainCategory, selectedSubCategory);
    return selectedProducts[categoryKey] || null;
  }

  const getCurrentProducts = useCallback(() => {
    if (viewState === 'main' || !selectedMainCategory || !selectedSubCategory) {
      return [];
    }

    const products = productData[selectedMainCategory]?.[selectedSubCategory] || [];
    return sortProductsByCompatibility(products, selectedProducts);
  }, [viewState, selectedMainCategory, selectedSubCategory, productData, selectedProducts, sortProductsByCompatibility]);

  const currentProducts = getCurrentProducts();
  const productInBuild = getProductInBuild();

  // Pre-compute compatibility status for all products
  const productsWithCompatibility = useMemo(() => {
    if (currentProducts.length === 0) return [];

    return currentProducts.map(product => ({
      ...product,
      _compatibilityStatus: getProductCompatibilityStatus(product, selectedProducts)
    }));
  }, [currentProducts, selectedProducts, getProductCompatibilityStatus]);

  // Product index synchronization
  useProductIndexSync({
    hasAccess,
    selectedMainCategory,
    selectedSubCategory,
    productInBuild,
    viewState,
    productData,
    sortProductsByCompatibility,
    selectedProducts,
    setSelectedProduct
  });

  // URL sync hook
  const { saveBuildToUrl } = useUrlSync(selectedProducts);

  // Product handlers hook - NOW WITH COMPULSORY HANDLING
  const {
    handleProductToggle,
    handleRemoveProduct,
    handleAddProductWithRequirements,
    handleSwitchEcosystem,
    handleSwitchDisplayEcosystem,
    handleNavigateToCategory,
    isProductInBuild
  } = useProductHandlers({
    toggleProduct,
    removeProduct,
    addProductWithRequirements,
    switchBrandEcosystem,
    switchDisplayEcosystem,
    sortProductsByCompatibility,
    selectedProducts,
    productData,
    setRequirementData,
    setShowRequirementPopup,
    setBrandConflictData,
    setShowBrandConflictPopup,
    setDisplayConflictData,
    setShowDisplayConflictPopup,
    setSizeConflictData,
    setShowSizeConflictPopup,
    setLockedMessage,
    setShowLockedPopup,
    setSelectedMainCategory,
    setViewState,
    setSelectedSubCategory,
    setShowMobileBuildSummary,
    setSelectedProduct,
    categoryConfig,
    // Compulsory handlers
    handleCompulsoryRemoval,
    handleProductAdded,
    pendingCompulsory,
    isSubcategoryCompulsory,
    isCategoryCompulsory,
    onTriggerCompulsoryShake: triggerCompulsoryShake
  });

  // Access control
  const handleAccessGranted = () => {
    setHasAccess(true);
  };

  // Hint arrow management
  const hideHintArrow = () => {
    setShowHintArrow(false);
    localStorage.setItem('vesaro-build-viewed', 'true');
  };

  const handleShowBuildSummary = () => {
    setShowMobileBuildSummary(true);
    if (showHintArrow) {
      hideHintArrow();
    }
  };

  // NEW: Handle contact sales with compulsory check
  const handleContactSales = useCallback(() => {
    // Check if all compulsory items are selected
    const missing = getMissingCompulsoryItems();
    
    if (missing.length > 0) {
      // Show missing compulsory popup
      setMissingCompulsoryItems(missing);
      setShowMissingCompulsoryPopup(true);
      return;
    }
    
    // All compulsory items present - show email form
    setShowEmailSalesForm(true);
  }, [getMissingCompulsoryItems]);

  // Reset banner dismissed state when pendingCompulsory changes
  useEffect(() => {
    if (pendingCompulsory) {
      setCompulsoryBannerDismissed(false);
    }
  }, [pendingCompulsory]);

  // Trigger scroll checks when content changes
  useEffect(() => {
    if (hasAccess) {
      const cleanup = triggerCategoryCheck();
      return cleanup;
    }
  }, [hasAccess, mainCategories, viewState, selectedMainCategory, selectedSubCategory, triggerCategoryCheck]);

  useEffect(() => {
    if (hasAccess) {
      const cleanup = triggerProductCheck();
      return cleanup;
    }
  }, [hasAccess, currentProducts, triggerProductCheck]);

  // Access control screen
  if (!hasAccess) {
    return <AccessControl onAccessGranted={handleAccessGranted} />;
  }

  // Check if we're in admin mode
  const showAdminSidebar = isAdminMode();

  // If showing price list, render that instead
  if (showPriceList) {
    return (
      <div className="min-h-[100dvh] w-full bg-[#E0E0E0]">
        {/* Content Layer */}
        <div className="flex min-h-[100dvh] w-full lg:grid lg:grid-cols-[16rem_1fr]">
          {/* ADMIN SIDEBAR */}
          <AdminConfigSidebar />

          {/* PRICE LIST GENERATOR */}
          <div className="flex-1 min-h-[100dvh] overflow-auto">
            <Header />
            <PriceListGenerator />
          </div>
        </div>
      </div>
    );
  }

  // Determine categories for navigation
  const displayCategories = viewState === 'main'
    ? getSelectableCategories(mainCategories)
    : getCurrentSubCategories();

  const selectedCategory = viewState === 'main' ? selectedMainCategory : selectedSubCategory;
  const onCategoryClick = viewState === 'main' ? handleMainCategoryClick : handleSubCategoryClick;

  return (
    <div className="min-h-[100dvh] w-full bg-[#F0F0F0]">
      {/* Content Layer */}
      <div className={`flex flex-col min-h-[100dvh] lg:h-screen w-full ${showAdminSidebar ? 'lg:grid lg:grid-cols-[16rem_1fr_20rem]' : 'lg:grid lg:grid-cols-[1fr_20rem]'}`}>

        {/* ADMIN SIDEBAR - Desktop only, Admin mode only */}
        {showAdminSidebar && <AdminConfigSidebar />}

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 flex flex-col min-h-[100dvh] lg:min-h-0 lg:h-screen lg:max-h-screen lg:overflow-hidden w-full">

          <Header />

          {/* Exit Configurator Button - Desktop only */}
          <div className="hidden lg:block absolute top-14 left-3 z-30">
            <button
              onClick={() => window.history.back()}
              className="px-2.5 py-1 bg-white hover:bg-gray-100 text-black text-xs font-medium rounded shadow transition-colors duration-200"
            >
              ← Exit
            </button>
          </div>

          {/* Compulsory Banner - shows when user must select an item */}
          <CompulsoryBanner
            pendingCompulsory={pendingCompulsory}
            shake={compulsoryBannerShake}
            dismissed={compulsoryBannerDismissed}
            onDismiss={() => setCompulsoryBannerDismissed(true)}
          />

          {/* CONTENT: Scrollable on mobile, flex container on desktop */}
          <div className="flex-1 overflow-auto lg:overflow-hidden lg:min-h-0 lg:flex lg:flex-col lg:items-center lg:justify-center bg-[#111111]">
            <div className="p-2 lg:p-4">

              {/* Product Viewer */}
              <ProductViewer
                currentProducts={currentProducts}
                selectedProduct={selectedProduct}
                viewState={viewState}
                mainCategories={mainCategories}
                selectedSubCategory={selectedSubCategory}
                selectedMainCategory={selectedMainCategory}
                productInBuild={productInBuild}
                baseConfig={storedBaseConfig}
                categoryConfig={categoryConfig}
              />

              {/* Base Simulator Title - Uses configuratorName for public, productName for admin */}
              {storedBaseConfig && (
                <div className="text-center -mt-1 sm:-mt-2 lg:-mt-4 mb-2 px-2">
                  <h1 className="text-white text-base sm:text-lg lg:text-xl font-semibold tracking-wide">
                    {isAdminMode()
                      ? (storedBaseConfig.productName ||
                         storedBaseConfig['Product Name'] ||
                         (storedBaseConfig.slug ? storedBaseConfig.slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : ''))
                      : (storedBaseConfig.configuratorName ||
                         storedBaseConfig.productName ||
                         storedBaseConfig['Product Name'] ||
                         (storedBaseConfig.slug ? storedBaseConfig.slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : ''))}
                  </h1>
                </div>
              )}

            </div>
          </div>

          {/* Navigation Section - Docked to bottom */}
          <div className="nav-docked-bottom">
            <NavigationSection
              loading={loading}
              error={error}
              viewState={viewState}
              selectedMainCategory={selectedMainCategory}
              selectedSubCategory={selectedSubCategory}
              onBack={handleBack}
              categoryScrollRef={categoryScrollRef}
              categoryScrollState={categoryScrollState}
              shouldShowArrows={shouldShowArrows}
              scrollCategoriesLeft={scrollCategoriesLeft}
              scrollCategoriesRight={scrollCategoriesRight}
              categories={displayCategories}
              selectedCategory={selectedCategory}
              onCategoryClick={onCategoryClick}
              products={productsWithCompatibility}
              selectedProduct={selectedProduct}
              productScrollRef={productScrollRef}
              productScrollState={productScrollState}
              scrollProducts={scrollProducts}
              isProductInBuild={(product) => isProductInBuild(product, selectedMainCategory, selectedSubCategory)}
              onProductSelect={setSelectedProduct}
              onProductToggle={handleProductToggle}
              onProductInfo={(product) => {
                setInfoPopupProduct(product);
                setShowInfoPopup(true);
              }}
              productInBuild={productInBuild}
              categoryConfig={categoryConfig}
              hasRealSubCategories={hasRealSubCategories}
              pendingCompulsory={pendingCompulsory}
              getCompulsoryStatus={getCompulsoryStatus}
            />
          </div>

          {/* MOBILE ONLY: Footer with Build Summary Button */}
          <MobileFooter
            selectedProducts={selectedProducts}
            totalPrice={totalPrice}
            showHintArrow={showHintArrow}
            onDismissHint={hideHintArrow}
            onShowBuildSummary={handleShowBuildSummary}
          />
        </div>

        {/* DESKTOP ONLY: Build Summary Sidebar */}
        <DesktopSidebar
          selectedProducts={selectedProducts}
          mainCategories={mainCategories}
          categoryConfig={categoryConfig}
          totalPrice={totalPrice}
          totalTestPrice={totalTestPrice}
          onRemoveProduct={handleRemoveProduct}
          onNavigateToCategory={handleNavigateToCategory}
          onShareBuild={saveBuildToUrl}
          onContactSales={handleContactSales}
        />
      </div>

      {/* All Popups */}
      <PopupManager
        showInfoPopup={showInfoPopup}
        infoPopupProduct={infoPopupProduct}
        selectedSubCategory={selectedSubCategory}
        categoryConfig={categoryConfig}
        onCloseInfo={() => {
          setShowInfoPopup(false);
          setInfoPopupProduct(null);
        }}
        showRequirementPopup={showRequirementPopup}
        requirementData={requirementData}
        onAddAllRequirements={() => handleAddProductWithRequirements(requirementData.product, requirementData.missingProducts, selectedMainCategory, selectedSubCategory)}
        onCloseRequirement={() => {
          setShowRequirementPopup(false);
          setRequirementData(null);
        }}
        showBrandConflictPopup={showBrandConflictPopup}
        brandConflictData={brandConflictData}
        selectedProducts={selectedProducts}
        onNavigateToCategory={handleNavigateToCategory}
        onSwitchEcosystem={handleSwitchEcosystem}
        onCloseBrandConflict={() => {
          setShowBrandConflictPopup(false);
          setBrandConflictData(null);
        }}
        showDisplayConflictPopup={showDisplayConflictPopup}
        displayConflictData={displayConflictData}
        onSwitchDisplayEcosystem={handleSwitchDisplayEcosystem}
        onCloseDisplayConflict={() => {
          setShowDisplayConflictPopup(false);
          setDisplayConflictData(null);
        }}
        showSizeConflictPopup={showSizeConflictPopup}
        sizeConflictData={sizeConflictData}
        onCloseSizeConflict={() => {
          setShowSizeConflictPopup(false);
          setSizeConflictData(null);
        }}
        showLockedPopup={showLockedPopup}
        lockedMessage={lockedMessage}
        onCloseLocked={() => {
          setShowLockedPopup(false);
          setLockedMessage('');
        }}
        showMobileBuildSummary={showMobileBuildSummary}
        mainCategories={mainCategories}
        totalPrice={totalPrice}
        totalTestPrice={totalTestPrice}
        onCloseMobileBuildSummary={() => setShowMobileBuildSummary(false)}
        onRemoveProduct={handleRemoveProduct}
        onShareBuild={saveBuildToUrl}
        onContactSales={handleContactSales}
        showEmailSalesForm={showEmailSalesForm}
        onCloseEmailSales={() => setShowEmailSalesForm(false)}
        // NEW: Missing compulsory popup
        showMissingCompulsoryPopup={showMissingCompulsoryPopup}
        missingCompulsoryItems={missingCompulsoryItems}
        onCloseMissingCompulsory={() => {
          setShowMissingCompulsoryPopup(false);
          setMissingCompulsoryItems([]);
        }}
      />

      {/* Enhanced responsive styles */}
      <style>{`
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow-x: hidden;
          background: #000000 !important;
        }

        #root {
          width: 100%;
          min-height: 100vh;
          min-height: 100dvh;
          background: #000000 !important;
        }

        @media (max-width: 1023px) {
          html, body, #root {
            height: 100%;
            min-height: 100vh;
            min-height: 100dvh;
            background: #000000 !important;
          }

          body {
            position: fixed;
            width: 100%;
            background: #000000 !important;
          }
        }

        @media (min-width: 1024px) {
          body {
            position: static !important;
            height: auto !important;
          }

          #root {
            height: 100vh !important;
          }
        }

        /* Small laptop screens - constrain product viewer height */
        @media (min-width: 1024px) and (max-height: 800px) {
          .product-viewer-responsive {
            max-height: calc(100vh - 460px) !important;
            aspect-ratio: 4/3;
            width: auto !important;
          }
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        @keyframes gentleBounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0, -8px, 0);
          }
          70% {
            transform: translate3d(0, -4px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }

        .animate-bounce {
          animation: gentleBounce 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}